import React, { useEffect, useState, useRef } from 'react';
import { PitchDeck } from './pages/PitchDeck';
import { AdvantageProduct } from './pages/AdvantageProduct';
import { SummitProduct } from './pages/SummitProduct';
import { SystemPage } from './pages/SystemPage';
import { LeadershipPage } from './pages/LeadershipPage';
import { LayoutGrid, FileText, Mountain, Calculator, Lock, Crown } from 'lucide-react';
type ViewType = 'deck' | 'advantage' | 'summit' | 'system' | 'leadership';
export function App() {
  // ALL hooks at the top
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [view, setView] = useState<ViewType>('deck');
  // Scroll position management
  const scrollPositions = useRef<Record<string, number>>({});
  // Check auth on mount
  useEffect(() => {
    setIsClient(true);
    const authStatus = localStorage.getItem('site_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  // Restore scroll position when view changes
  useEffect(() => {
    // Small timeout to ensure DOM is ready
    const timer = setTimeout(() => {
      window.scrollTo(0, scrollPositions.current[view] || 0);
    }, 0);
    return () => clearTimeout(timer);
  }, [view]);
  // Handler functions
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'HIRE100k' || password === 'TheManny') {
      localStorage.setItem('site_authenticated', 'true');
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };
  const handleViewChange = (newView: ViewType) => {
    // Save current scroll position before switching
    scrollPositions.current[view] = window.scrollY;
    setView(newView);
  };
  // Show loading during initial mount
  if (!isClient) {
    return <div className="min-h-screen bg-[#0A0F1C]" data-id="element-0"></div>;
  }
  // Show login if not authenticated
  if (!isAuthenticated) {
    return <div className="min-h-screen w-full bg-[#0A0F1C] flex items-center justify-center p-4 sm:p-6" data-id="element-1">
        <div className="w-full max-w-md" data-id="element-2">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 sm:p-8 shadow-2xl" data-id="element-3">
            <div className="flex justify-center mb-6" data-id="element-4">
              <div className="w-16 h-16 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 flex items-center justify-center" data-id="element-5">
                <Lock className="w-8 h-8 text-[#7FE0C3]" data-id="element-6" />
              </div>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-white text-center mb-2" data-id="element-7">
              Welcome to HIRE
            </h1>
            <p className="text-sm sm:text-base text-slate-400 text-center mb-8" data-id="element-8">
              Please enter your access code to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-9">
              <div data-id="element-10">
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2" data-id="element-11">
                  Access Code
                </label>
                <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#7FE0C3] focus:border-transparent transition-all text-base" placeholder="Enter access code" autoFocus data-id="element-12" />
              </div>

              {error && <div className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 rounded-lg py-2" data-id="element-13">
                  {error}
                </div>}

              <button type="submit" className="w-full px-4 py-3 bg-[#7FE0C3] hover:bg-[#5ACCA8] text-[#163A5F] font-medium rounded-lg transition-all transform active:scale-95" data-id="element-14">
                Access Site
              </button>
            </form>
          </div>
        </div>
      </div>;
  }
  // Main content
  return <div className="relative" data-id="element-15">
      {view === 'deck' && <PitchDeck data-id="element-16" />}
      {view === 'advantage' && <AdvantageProduct data-id="element-17" />}
      {view === 'summit' && <SummitProduct data-id="element-18" />}
      {view === 'system' && <SystemPage data-id="element-19" />}
      {view === 'leadership' && <LeadershipPage data-id="element-20" />}

      {/* Left Side Button - Mobile Optimized (ROI only now) */}
      <div className="fixed top-3 left-3 sm:top-6 sm:left-6 z-[100]" data-id="element-21">
        {/* ROI Calculator Button */}
        <a href="https://calculator.thehireadvantage.co" target="_blank" rel="noopener noreferrer" className="px-3 py-2 sm:px-4 sm:py-2 bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-full shadow-xl text-slate-400 hover:text-white hover:border-[#7FE0C3]/50 transition-all flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium min-h-[44px]" data-id="element-22">
          <Calculator className="w-4 h-4" data-id="element-23" />
          <span className="hidden sm:inline" data-id="element-24">ROI</span>
        </a>
      </div>

      {/* Navigation Toggle - Mobile Optimized */}
      <div className="fixed top-3 right-3 sm:top-6 sm:right-6 z-[100] flex bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-full p-1 shadow-xl" data-id="element-25">
        <button onClick={() => handleViewChange('deck')} className={`p-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center transition-all min-h-[40px] min-w-[40px] justify-center ${view === 'deck' ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white'}`} title="Deck" data-id="element-26">
          <LayoutGrid className="w-4 h-4" data-id="element-27" />
          <span className="hidden lg:inline ml-2" data-id="element-28">Deck</span>
        </button>
        <button onClick={() => handleViewChange('advantage')} className={`p-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center transition-all min-h-[40px] min-w-[40px] justify-center ${view === 'advantage' ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white'}`} title="Advantage" data-id="element-29">
          <FileText className="w-4 h-4" data-id="element-30" />
          <span className="hidden lg:inline ml-2" data-id="element-31">Advantage</span>
        </button>
        <button onClick={() => handleViewChange('summit')} className={`p-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center transition-all min-h-[40px] min-w-[40px] justify-center ${view === 'summit' ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white'}`} title="Summit" data-id="element-32">
          <Mountain className="w-4 h-4" data-id="element-33" />
          <span className="hidden lg:inline ml-2" data-id="element-34">Summit</span>
        </button>
        <button onClick={() => handleViewChange('leadership')} className={`p-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium flex items-center transition-all min-h-[40px] min-w-[40px] justify-center ${view === 'leadership' ? 'bg-[#7FE0C3] text-[#163A5F] shadow-lg' : 'text-slate-400 hover:text-white'}`} title="Leadership" data-id="element-35">
          <Crown className="w-4 h-4" data-id="element-36" />
          <span className="hidden lg:inline ml-2" data-id="element-37">Leadership</span>
        </button>
      </div>
    </div>;
}